import React, { useCallback, useEffect, useState } from 'react';
import { useGetFileFromStorage } from '../../../hooks/useGetFileFromStorage';
import Spinner from '../../../components/Spinner';

type ChatMediaImageThumbProps = {
  url: string;
  mediaId: string;
  appointmentId: string;
  alt?: string;
  onClick?: (resolvedUrl: string) => void;
  className?: string;
};

export const ChatMediaImageThumb: React.FC<ChatMediaImageThumbProps> = ({
  url,
  mediaId,
  appointmentId,
  alt = 'media',
  onClick,
  className,
}) => {
  const { getUrlFileFromVonageStorage } = useGetFileFromStorage();

  const [resolvedUrl, setResolvedUrl] = useState(url);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setResolvedUrl(url);
    setHasTriedFallback(false);
  }, [url, mediaId, appointmentId]);

  const handleError = useCallback(() => {
    if (!mediaId || !appointmentId) return;
    if (hasTriedFallback) return;
    setLoading(true);

    setHasTriedFallback(true);

    getUrlFileFromVonageStorage(mediaId, appointmentId)
      .then((newUrl) => {
        if (newUrl) setResolvedUrl(newUrl);
      })
      .finally(() => setLoading(false))
      .catch(() => {
        console.error('Failed to fetch file from storage');
      });
  }, [getUrlFileFromVonageStorage, appointmentId, hasTriedFallback, mediaId]);

  const handleClick = useCallback(() => {
    onClick?.(resolvedUrl);
  }, [onClick, resolvedUrl]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={
        className ??
        'relative w-full aspect-square overflow-hidden rounded-[10px] transition'
      }
    >
      {loading ? (
        <Spinner />
      ) : (
        <img
          src={resolvedUrl}
          alt={alt}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={handleError}
        />
      )}
    </button>
  );
};
